import { get, post, put, del } from './client'

/**
 * 与后端模型元数据 DTO 对齐
 */
export interface ModelMeta {
  id: number
  model_name: string
  description?: string
  icon?: string
  tags?: string
  vendor_id?: number
  endpoints?: string
  status: number
  sync_official: number
  created_time: number
  updated_time: number
  name_rule: number
  bound_channels?: Array<{ name: string; type: number }>
  enable_groups?: string[]
  quota_types?: number[]
  matched_models?: string[]
  matched_count?: number
}

export interface VendorMeta {
  id: number
  name: string
  description?: string
  icon?: string
  status: number
  created_time: number
  updated_time: number
}

export interface ModelListResponse {
  items: ModelMeta[]
  total: number
  page: number
  page_size: number
  vendor_counts?: Record<string, number>
}

export interface SyncUpstreamResult {
  created_models?: number
  created_vendors?: number
  updated_models?: number
  skipped_models?: unknown[]
  created_list?: string[]
  updated_list?: string[]
  [key: string]: unknown
}

export interface SyncUpstreamConflictField {
  field: string
  local: unknown
  upstream: unknown
}

export interface SyncUpstreamConflict {
  model_name: string
  fields: SyncUpstreamConflictField[]
}

export interface SyncUpstreamPreview {
  missing: string[]
  conflicts: SyncUpstreamConflict[]
  source?: {
    locale?: string
    models_url?: string
    vendors_url?: string
  }
}

export interface SyncUpstreamOverwrite {
  model_name: string
  fields: string[]
}

/** 新建/编辑模型请求体（与后端 model.Model 对齐） */
export interface ModelMetaPayload {
  id?: number
  model_name: string
  description?: string
  icon?: string
  tags?: string
  vendor_id?: number
  endpoints?: string
  status: number
  sync_official: number
  name_rule: number
}

/** 新建/编辑供应商请求体 */
export interface VendorMetaPayload {
  id?: number
  name: string
  description?: string
  icon?: string
  status: number
}

/**
 * 供应商列表
 * GET /api/vendors/
 */
export async function listVendorsApi(params?: { page_size?: number }): Promise<{ items: VendorMeta[]; total: number }> {
  return get('/api/vendors/', { page_size: params?.page_size ?? 1000 })
}

/**
 * 分页拉取模型元数据
 * GET /api/models/
 */
export async function listModelsMetaApi(params: {
  p: number
  page_size: number
}): Promise<ModelListResponse> {
  return get<ModelListResponse>('/api/models/', params)
}

/**
 * 搜索模型（关键词 / 供应商 Id）
 * GET /api/models/search
 */
export async function searchModelsMetaApi(params: {
  p: number
  page_size: number
  keyword?: string
  vendor?: string | number
}): Promise<ModelListResponse> {
  return get<ModelListResponse>('/api/models/search', {
    p: params.p,
    page_size: params.page_size,
    ...(params.keyword ? { keyword: params.keyword } : {}),
    ...(params.vendor !== undefined && params.vendor !== '' && params.vendor !== 'all'
      ? { vendor: params.vendor }
      : {}),
  })
}

/**
 * 预览上游模型与本地模型差异
 * GET /api/models/sync_upstream/preview
 */
export async function syncUpstreamPreviewApi(params?: { locale?: string }): Promise<SyncUpstreamPreview> {
  return get<SyncUpstreamPreview>('/api/models/sync_upstream/preview', params)
}

/**
 * 从上游同步模型与供应商，可选择覆盖本地字段
 * POST /api/models/sync_upstream
 */
export async function syncUpstreamModelsApi(body?: {
  locale?: string
  overwrite?: SyncUpstreamOverwrite[]
}): Promise<SyncUpstreamResult> {
  return post<SyncUpstreamResult>('/api/models/sync_upstream', body || {})
}

/**
 * 仅更新启用状态（与 useModelsData 一致）
 * PUT /api/models/?status_only=true
 */
export async function updateModelStatusOnlyApi(id: number, status: 0 | 1): Promise<void> {
  return put('/api/models/?status_only=true', { id, status })
}

/**
 * 删除模型
 * DELETE /api/models/:id
 */
export async function deleteModelMetaApi(id: number): Promise<void> {
  return del(`/api/models/${id}`)
}

/**
 * 获取单条模型元数据
 * GET /api/models/:id
 */
export async function getModelMetaApi(id: number): Promise<ModelMeta> {
  return get<ModelMeta>(`/api/models/${id}`)
}

/**
 * 新建模型元数据
 * POST /api/models/
 */
export async function createModelMetaApi(body: ModelMetaPayload): Promise<ModelMeta> {
  return post<ModelMeta>('/api/models/', body)
}

/**
 * 全字段更新模型元数据
 * PUT /api/models/
 */
export async function updateModelMetaApi(body: ModelMetaPayload & { id: number }): Promise<ModelMeta> {
  return put<ModelMeta>('/api/models/', body)
}

/**
 * 获取已启用但无元数据的模型名列表
 * GET /api/models/missing
 */
export async function getMissingModelsApi(): Promise<string[]> {
  return get<string[]>('/api/models/missing')
}

/**
 * 搜索供应商
 * GET /api/vendors/search
 */
export async function searchVendorsApi(params: {
  p?: number
  page_size?: number
  keyword?: string
}): Promise<{ items: VendorMeta[]; total: number }> {
  return get('/api/vendors/search', params)
}

/**
 * 获取单条供应商
 * GET /api/vendors/:id
 */
export async function getVendorApi(id: number): Promise<VendorMeta> {
  return get<VendorMeta>(`/api/vendors/${id}`)
}

/**
 * 新建供应商
 * POST /api/vendors/
 */
export async function createVendorApi(body: VendorMetaPayload): Promise<VendorMeta> {
  return post<VendorMeta>('/api/vendors/', body)
}

/**
 * 更新供应商
 * PUT /api/vendors/
 */
export async function updateVendorApi(body: VendorMetaPayload & { id: number }): Promise<VendorMeta> {
  return put<VendorMeta>('/api/vendors/', body)
}

/**
 * 删除供应商
 * DELETE /api/vendors/:id
 */
export async function deleteVendorApi(id: number): Promise<void> {
  return del(`/api/vendors/${id}`)
}
